import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import Cars from './pages/Cars';
import CarDetail from './pages/CarDetail';
import EditCar from './pages/EditCar';
import Repairs from './pages/Repairs';
import RepairDetail from './pages/RepairDetail';
import CreateRepair from './pages/CreateRepair';
import EditRepair from './pages/EditRepair';
import Invoices from './pages/Invoices';
import InvoiceDetail from './pages/InvoiceDetail';
import ServiceTemplates from './pages/ServiceTemplates';
import ServiceTemplateDetail from './pages/ServiceTemplateDetail';
import CreateServiceTemplate from './pages/CreateServiceTemplate';
import HourlyRates from './pages/HourlyRates';
import HourlyRateDetail from './pages/HourlyRateDetail';
import Locations from './pages/Locations';
import LocationDetail from './pages/LocationDetail';
import Inventory from './pages/Inventory';
import InventoryDetail from './pages/InventoryDetail';
import Purchases from './pages/Purchases';
import PurchaseDetail from './pages/PurchaseDetail';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Login from './pages/Login';
import LandingPage from './pages/LandingPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminTenants from './pages/AdminTenants';
import AdminUsers from './pages/AdminUsers';
import ChangePassword from './pages/ChangePassword';
import CustomerPortal from './pages/CustomerPortal';
import CustomerRegister from './pages/CustomerRegister';
import CustomerLogin from './pages/CustomerLogin';
import CustomerDashboard from './pages/CustomerDashboard';
import CustomerCarDetail from './pages/CustomerCarDetail';
import CustomerEditCar from './pages/CustomerEditCar';
import Appointments from './pages/Appointments';
import AppointmentDetail from './pages/AppointmentDetail';
import Roles from './pages/Roles';
import Team from './pages/Team';
import AccountDetail from './pages/AccountDetail';
import MaintenancePlans from './pages/MaintenancePlans';
import Fleet from './pages/Fleet';
import EditFleet from './pages/EditFleet';
import VerifyEmail from './pages/VerifyEmail';
import Tags from './pages/Tags';
import Equipment from './pages/Equipment';
import Impressum from './pages/Impressum';
import AGBs from './pages/AGBs';
import Datenschutz from './pages/Datenschutz';
import Contact from './pages/Contact';
import Support from './pages/Support';
import Documentation from './pages/Documentation';
import ApiTokens from './pages/ApiTokens';
import ApiDocumentation from './pages/ApiDocumentation';
import ProtectedRoute from './components/ProtectedRoute';
import { NotificationProvider } from './components/Notification';
import Spinner from './components/Spinner';
import { api } from './services/api';
import { config } from './config';

function App() {
  const [tenantId, setTenantId] = useState(() => {
    return localStorage.getItem('tenantId') || '';
  });
  const [tenants, setTenants] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      const storedTenantId = localStorage.getItem('tenantId') || user?.tenantId;
      if (storedTenantId && storedTenantId !== tenantId) {
        setTenantId(storedTenantId);
      }
      loadTenants();
    } else if (user && user.role === 'admin') {
      setLoading(false);
    }
  }, [user, tenantId]);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (!token) {
        setLoading(false);
        return;
      }

      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
          if (parsedUser.role !== 'admin' && parsedUser.tenantId) {
            const storedTenantId = localStorage.getItem('tenantId');
            if (storedTenantId) {
              setTenantId(storedTenantId);
            }
          }
        } catch (e) {
          console.error('Error parsing stored user:', e);
        }
      }

      const response = await fetch(`${config.API_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('tenantId');
          setUser(null);
        }
        setLoading(false);
        return;
      }

      const data = await response.json();
      setUser(data);
      localStorage.setItem('user', JSON.stringify(data));

      if (data.role !== 'admin' && data.tenantId) {
        localStorage.setItem('tenantId', data.tenantId);
        setTenantId(data.tenantId);
      }
    } catch (error) {
      console.error('Auth check error:', error);
      const token = localStorage.getItem('token');
      if (token) {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            setUser(parsedUser);
            if (parsedUser.role !== 'admin' && parsedUser.tenantId) {
              const storedTenantId = localStorage.getItem('tenantId');
              if (storedTenantId) {
                setTenantId(storedTenantId);
              }
            }
          } catch (e) {
            console.error('Error parsing stored user:', e);
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const loadTenants = async () => {
    try {
      const data = await api.get('/tenants');
      setTenants(data);
      if (data.length > 0) {
        const currentTenantId = localStorage.getItem('tenantId') || user?.tenantId;
        const validTenant = data.find(t => t.id === currentTenantId) || data[0];
        const newTenantId = validTenant.id;
        if (newTenantId !== tenantId) {
          setTenantId(newTenantId);
          localStorage.setItem('tenantId', newTenantId);
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading tenants:', error);
      setLoading(false);
    }
  };

  const handleTenantChange = (newTenantId) => {
    setTenantId(newTenantId);
    localStorage.setItem('tenantId', newTenantId);
  };

  const isTenantUserReady = user && user.role !== 'admin' && tenantId && tenants.length > 0;
  const isWaitingForTenants = user && user.role !== 'admin' && !tenantId && loading;
  const isOnLoginPage = window.location.pathname === '/login';
  const isTenantAdmin = user && user.role === 'tenantAdmin';

  if ((loading || isWaitingForTenants) && !isOnLoginPage) {
    return <Spinner />;
  }


  return (
    <NotificationProvider>
      <Router>
        <Routes>
        <Route path="/landingpage" element={<LandingPage />} />
        <Route path="/customer/:token" element={<CustomerPortal />} />
        <Route path="/customer-register" element={<CustomerRegister />} />
        <Route path="/customer-login" element={<CustomerLogin />} />
        <Route path="/customer-dashboard" element={<CustomerDashboard />} />
        <Route path="/customer-cars/:id" element={<CustomerCarDetail />} />
        <Route path="/customer-cars/:id/edit" element={<CustomerEditCar />} />
        <Route path="/login" element={<Login />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/impressum" element={<Impressum />} />
        <Route path="/agbs" element={<AGBs />} />
        <Route path="/datenschutz" element={<Datenschutz />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/support" element={<Support />} />
        <Route path="/documentation" element={<Documentation />} />
        <Route path="/api-documentation" element={<ApiDocumentation />} />
        <Route
          path="/change-password"
          element={
            <ProtectedRoute>
              <ChangePassword />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin={true}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/tenants"
          element={
            <ProtectedRoute requireAdmin={true}>
              <AdminTenants />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute requireTenantAdmin={true}>
              <AdminUsers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              {isTenantUserReady || isTenantAdmin ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Navigate to="/login" replace />
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              {isTenantUserReady || isTenantAdmin ? (
                <Layout tenantId={tenantId} tenants={tenants} onTenantChange={handleTenantChange} user={user}>
                  <Routes>
                    <Route path="/dashboard" element={<Dashboard tenantId={tenantId} />} />
                    <Route path="/customers" element={<Customers tenantId={tenantId} />} />
                    <Route path="/customers/:id" element={<CustomerDetail tenantId={tenantId} />} />
                    <Route path="/cars" element={<Cars tenantId={tenantId} />} />
                    <Route path="/cars/:id" element={<CarDetail tenantId={tenantId} />} />
                    <Route path="/cars/:id/edit" element={<EditCar tenantId={tenantId} />} />
                    <Route path="/repairs" element={<Repairs tenantId={tenantId} />} />
                    <Route path="/repairs/:id" element={<RepairDetail tenantId={tenantId} />} />
                    <Route path="/repairs/new" element={<CreateRepair tenantId={tenantId} />} />
                    <Route path="/repairs/:id/edit" element={<EditRepair tenantId={tenantId} />} />
                    <Route path="/invoices" element={<Invoices tenantId={tenantId} />} />
                    <Route path="/invoices/:id" element={<InvoiceDetail tenantId={tenantId} />} />
                    <Route path="/service-templates" element={<ServiceTemplates tenantId={tenantId} />} />
                    <Route path="/service-templates/new" element={<CreateServiceTemplate tenantId={tenantId} />} />
                    <Route path="/service-templates/:id" element={<ServiceTemplateDetail tenantId={tenantId} />} />
                    <Route path="/hourly-rates" element={<HourlyRates tenantId={tenantId} />} />
                    <Route path="/hourly-rates/:id" element={<HourlyRateDetail tenantId={tenantId} />} />
                    <Route path="/locations" element={<Locations tenantId={tenantId} />} />
                    <Route path="/locations/:id" element={<LocationDetail tenantId={tenantId} />} />
                    <Route path="/inventory" element={<Inventory tenantId={tenantId} />} />
                    <Route path="/inventory/:id" element={<InventoryDetail tenantId={tenantId} />} />
                    <Route path="/purchases" element={<Purchases tenantId={tenantId} />} />
                    <Route path="/purchases/:id" element={<PurchaseDetail tenantId={tenantId} />} />
                    <Route path="/reports" element={<Reports tenantId={tenantId} />} />
                    <Route path="/settings" element={<Settings tenantId={tenantId} />} />
                    <Route path="/api-tokens" element={<ApiTokens tenantId={tenantId} />} />
                    <Route path="/appointments" element={<Appointments tenantId={tenantId} />} />
                    <Route path="/appointments/:id" element={<AppointmentDetail tenantId={tenantId} />} />
                    <Route path="/roles" element={<Roles tenantId={tenantId} />} />
                    <Route path="/team" element={<Team tenantId={tenantId} />} />
                    <Route path="/account/:id" element={<AccountDetail tenantId={tenantId} />} />
                    <Route path="/maintenance-plans" element={<MaintenancePlans tenantId={tenantId} />} />
                    <Route path="/tags" element={<Tags tenantId={tenantId} />} />
                    <Route path="/equipment" element={<Equipment tenantId={tenantId} />} />
                    <Route path="/fleet/inventory" element={<Fleet tenantId={tenantId} />} />
                    <Route path="/fleet/sales" element={<Fleet tenantId={tenantId} />} />
                    <Route path="/fleet" element={<Fleet tenantId={tenantId} />} />
                    <Route path="/fleet/new" element={<EditFleet tenantId={tenantId} />} />
                    <Route path="/fleet/:id/edit" element={<EditFleet tenantId={tenantId} />} />
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                </Layout>
              ) : (
                <Navigate to={user?.role === 'admin' ? '/admin' : '/login'} replace />
              )}
            </ProtectedRoute>
          }
        />
        </Routes>
      </Router>
    </NotificationProvider>
  );
}

export default App;

